import { Checkbox, Paper, Stack, Text } from '@mantine/core';
import { AvatarMobile } from 'components/AvatarMobile';
import { Nothing } from 'components/Nothing';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { objTextColor, Status } from 'ui/CustomerOrderItem';
import { addSpaceNumber } from 'utils/addSpaceNumber';
import { getDateAndMonth } from 'utils/date';
import styles from './OrdersListMobile.module.scss';
import { ReactComponent as IconAlert } from './img/iconAlertOutline.svg';
import { ReactComponent as IconClose } from './img/iconClose.svg';
import { ReactComponent as IconDone } from './img/iconDone.svg';
import { ReactComponent as IconDraft } from './img/iconDraft.svg';
import { ReactComponent as IconTime } from './img/iconTime.svg';
import { ReactComponent as IconProblem } from '../../assets/img/Employed/iconProblem.svg';
import { TextWithTooltip } from 'hooks/TextWithTooltip';

interface OrdersListMobileTypes {
  data: any;
  intersectionComponent: JSX.Element | null;
  withCheckbox?: boolean;
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
}

export const objIcons: any = {
  moderation_in_progress: IconTime,
  moderation_failed: IconClose,
  worker_invited_by_customer: IconAlert,
  worker_approved_order: IconTime,
  worker_set_volume: IconTime,
  customer_set_volume: IconTime,
  worker_finished_order: IconAlert,
  work_success: IconAlert,
  act_signed_by_worker: IconAlert,
  payment_in_progress: IconTime,
  act_signed_by_customer: IconTime,
  payment_failed: IconClose,
  payment_returned: IconClose,
  payment_success: IconDone,
  order_canceled: IconClose,
  order_draft: IconDraft,
  order_published: IconTime,
  worker_rejected_by_customer: IconTime,
  order_canceled_by_platform: IconClose,
};

export const OrdersListMobile: FC<OrdersListMobileTypes> = ({
  data,
  intersectionComponent,
  withCheckbox = false,
  selected,
  setSelected,
}) => {
  const role = localStorage.getItem('role') || 'WorkSupport';
  const navigate = useNavigate();

  const handleNaviage = (id: string) => {
    navigate(`/orders/${id}`);
  };

  return (
    <Stack align='stretch' justify='center' gap='sm' mb='20px'>
      {data?.length ? (
        data.map((item: any) => (
          <Paper
            shadow='0'
            p='12px'
            radius='md'
            key={item.id}
            style={{ cursor: 'pointer' }}
            onClick={() =>
              withCheckbox
                ? setSelected((prev) => {
                    if (prev.includes(item.id)) {
                      return prev.filter((i) => i !== item.id);
                    } else {
                      return [...prev, item.id];
                    }
                  })
                : handleNaviage(item?.id)
            }>
            <div style={{ gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div className={styles.second}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      padding: '0px 12px 0px 0px',
                    }}>
                    <div className={styles.name}>
                      <AvatarMobile
                        size={24}
                        name={item?.worker?.name}
                        surname={item?.worker?.surname}
                        avatar={item?.worker?.avatar}
                        isDeleted={item?.worker?.is_deleted}
                      />
                      <TextWithTooltip
                        content={
                          !item?.worker?.surname
                            ? item?.worker?.name
                            : item?.worker?.patronymic
                            ? `${item?.worker?.surname} ${item?.worker?.name} ${item?.worker?.patronymic}`
                            : `${item?.worker?.surname} ${item?.worker?.name}`
                        }
                        className={styles.truncate}
                      />
                    </div>
                  </div>
                </div>
                {withCheckbox ? (
                  selected.includes(item?.id) ? (
                    <div className={styles.checkbox_active}>
                      <Checkbox
                        color='#002CFB'
                        checked={selected.includes(item.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                        }}
                        styles={{
                          input: {
                            border: selected.includes(item.id)
                              ? '2px solid #002CFB'
                              : '2px solid rgba(173, 191, 223, 0.4)',
                            cursor: 'pointer',
                          },
                        }}
                      />
                    </div>
                  ) : (
                    <div className={styles.checkbox}></div>
                  )
                ) : null}
              </div>

              <div className={styles.group}>
                <Text lh='22px'>№{item?.number}</Text>•
                <Text truncate='end' lh='22px'>
                  {getDateAndMonth(item?.created_at)}
                </Text>
                •<Text lh='22px'>{addSpaceNumber(item?.amount)} ₽</Text>
              </div>

              <div className={styles.group_status}>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <p className={styles.text}>{item?.order_type}</p>
                </div>
                <div className={styles.status}>
                  <Status
                    title={item.status?.text}
                    color={objTextColor[item.status?.value] || '#000'}
                    Icon={objIcons[item.status?.value] || null}
                  />
                </div>
              </div>
            </div>
          </Paper>
        ))
      ) : (
        <Nothing style={{ height: '320px' }} title='Ничего не найдено' />
      )}
      {intersectionComponent}
    </Stack>
  );
};
